/**
 * useWorkflowBuilderStatus
 *
 * Hook to poll workflow builder status. Polls every 2s while the builder
 * is in any active state (pending/exploring/validating) and stops on
 * terminal states (completed/failed/cancelled) or when no builder job
 * exists. The derived `isGenerating` flag — used to gate the FlowDesigner
 * generation interstitial — is true only for pending/exploring; the
 * separate 'validating' phase runs the just-built workflow headlessly,
 * and the canvas takes over via useWorkflowExecution.checkForActiveRun.
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type WorkflowBuilderStatus =
  | 'none'
  | 'pending'
  | 'exploring'
  | 'validating'
  | 'completed'
  | 'failed'
  | 'cancelled';

type BuilderStatusResponse = {
  ok: boolean;
  status: WorkflowBuilderStatus;
  streamId?: string;
  nodeCount: number;
  error?: string;
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useWorkflowBuilderStatus(workflowId: string | null | undefined) {
  const query = useQuery({
    queryKey: ['workflow-builder-status', workflowId],
    queryFn: async (): Promise<BuilderStatusResponse> => {
      if (!workflowId) {
        return { ok: true, status: 'none', nodeCount: 0 };
      }

      const response = await fetch(`${API_URL}/workflows/${workflowId}/builder-status`, {
        credentials: 'include',
      });

      if (!response.ok) {
        // 404 is expected when the workflow doesn't exist in the current org
        if (response.status === 404 || response.status === 403) {
          return { ok: true, status: 'none' as const, nodeCount: 0 };
        }
        throw await toApiError(response, 'Failed to fetch builder status');
      }

      return response.json();
    },
    enabled: !!workflowId,
    // Poll every 2s while actively generating
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Keep polling while pending, exploring, or validating
      if (status === 'pending' || status === 'exploring' || status === 'validating') {
        return 2000;
      }
      // Stop polling for terminal states
      return false;
    },
    // Refetch on window focus only if actively generating
    refetchOnWindowFocus: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'exploring' || status === 'validating';
    },
    staleTime: 1000,
  });

  const status = query.data?.status ?? 'none';
  // 'validating' deliberately excluded. That phase runs the just-built
  // workflow headlessly to verify it works, which can take many minutes for
  // non-trivial flows. The FlowDesigner canvas already picks up the active
  // validation run via useWorkflowExecution.checkForActiveRun and shows it
  // with regular per-node run indicators — better UX than hiding everything
  // behind the generation interstitial for that whole window.
  // We still poll while 'validating' (refetchInterval above) so we detect
  // the terminal transition; this flag just controls modal-mounting.
  const isGenerating = status === 'pending' || status === 'exploring';

  return {
    status,
    streamId: query.data?.streamId ?? null,
    nodeCount: query.data?.nodeCount ?? 0,
    error: query.data?.error ?? null,
    isGenerating,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
